import { useEffect, useMemo, useState } from 'react'
import { ResetPasswordPage } from './auth/ResetPasswordPage'
import { SlpGallery } from './components/slp/Gallery'
import { navigate, usePath } from './lib/router'
import { JoinPage } from './pages/JoinPage'
import { NewSessionPage } from './pages/NewSessionPage'
import { RoomPage } from './pages/RoomPage'
import { RtConnectionProvider } from './rt/use-rt-connection'
import './styles/slp.css'

/**
 * Hand-rolled router — five routes:
 *   /new             host creates a session (Supabase login)
 *   /join/:token     guest landing + pre-flight (connects phase='preflight')
 *   /room            in-session surface (join payload via sessionStorage)
 *   /reset-password  password-recovery landing (auth trio, F4)
 *   /gallery         dev-only SLP component showcase (rt UI v2)
 *
 * G1 (spec v2.8 全应用桌面铁律): the WHOLE app is a desktop professional
 * tool — mobile/tablet UAs and sub-900px viewports get an interstitial on
 * every route instead of a squeezed layout.
 */

const MOBILE_UA = /iPhone|iPad|iPod|Android|Mobile|Silk|Tablet|webOS|BlackBerry|Opera Mini/i

function DesktopGate() {
  return (
    <div
      data-testid="rt-desktop-gate"
      className="flex min-h-screen items-center justify-center bg-[var(--slp-page)] px-6"
    >
      <div className="w-full max-w-[420px] overflow-hidden rounded-md border border-[var(--slp-border)] bg-white shadow-sm">
        <header className="flex h-[26px] items-center justify-center bg-[var(--slp-blue)] text-[12px] font-semibold text-white">
          OHEAR Session
        </header>
        <div className="flex flex-col gap-2 p-4 text-[13px] leading-relaxed text-[var(--slp-text)]">
          <p className="font-semibold">
            OHEAR Session 是桌面专业工具，请使用桌面 Chrome（≥1280px 窗口）打开。
          </p>
          <p className="text-[var(--slp-text-dim)]">
            OHEAR Session is a desktop professional tool. Please open this link in desktop Google
            Chrome (or Microsoft Edge) with a window at least 1280&nbsp;px wide — remote recording
            needs a real computer, a real microphone and real disk space.
          </p>
        </div>
      </div>
    </div>
  )
}

export function App() {
  const path = usePath()

  // ---- desktop gate (v2.8): mobile/tablet UA OR viewport < 900 → block all
  const isMobileUa = useMemo(
    () => MOBILE_UA.test(navigator.userAgent) || (navigator.userAgentData?.mobile ?? false),
    [],
  )
  const [tooNarrow, setTooNarrow] = useState(() => window.innerWidth < 900)
  useEffect(() => {
    const onResize = () => setTooNarrow(window.innerWidth < 900)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Redirect unknown paths (and "/") to /new.
  const isKnown =
    path === '/new' ||
    path === '/room' ||
    path === '/gallery' ||
    path === '/reset-password' ||
    path.startsWith('/join/')
  useEffect(() => {
    if (!isKnown) navigate('/new', { replace: true })
  }, [isKnown])

  if (isMobileUa || tooNarrow) return <DesktopGate />

  let page: JSX.Element
  if (path.startsWith('/join/')) {
    const token = decodeURIComponent(path.slice('/join/'.length).replace(/\/+$/, ''))
    page = <JoinPage key={token} token={token} />
  } else if (path === '/room') page = <RoomPage />
  else if (path === '/reset-password') page = <ResetPasswordPage />
  else if (path === '/gallery') page = <SlpGallery />
  else page = <NewSessionPage />

  return <RtConnectionProvider>{page}</RtConnectionProvider>
}
